import type { ExtendedLibrary } from '@seen/core/models/library'
import { NoteLabel } from '@seen/core/models/noteLabel'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import {
  VISIT_NOTE_CATEGORIES,
  visitNoteCategoryToken,
} from '@seen/core/models/visitNoteCategory'
import {
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@seen/ui'
import { IconTag } from '@tabler/icons-react'
import { type JSX, useCallback, useMemo, useState } from 'react'
import { ComboboxMultiCommand } from '#src/components/core/combobox/ComboboxMulti.tsx'
import { LabelOption } from '#src/components/LabelOption.tsx'
import { partitionLabelSelection } from '#src/components/noteTemplateLabelSelection.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

// Right-click "Labels" submenu for a note template row: the same searchable
// label + category checklist the detail page exposes, persisting each toggle
// immediately via a targeted patch to `meta.tag` (labels) and `Library.topic[]`
// (categories). Takes the row's `ExtendedLibrary` (callers guard with
// `NoteTemplate.isInstance`) and rehydrates the typed model here so the parse
// only runs when this row's menu actually opens, not for every rendered row.
export function NoteTemplateLabelsContextMenuSub({
  template: library,
}: {
  template: ExtendedLibrary
}): JSX.Element {
  const template = useMemo(
    () => NoteTemplate.fromFhirResource(library.toFhirResource()),
    [library]
  )
  const { data: labels } = useSearch(NoteLabel, NoteLabel.baseSearchQuery())
  const { mutate: patchTemplate } = usePatchMutation<NoteTemplate>({
    // Serialize patches to the same template so rapid toggling can't race.
    mutation: { scope: { id: template.reference.reference } },
  })

  const options = useMemo(
    () => [
      ...(labels ?? []).map((label) => ({
        value: label.reference.reference,
        label: <LabelOption color={label.color} label={label.name} />,
        keywords: [label.name],
      })),
      ...VISIT_NOTE_CATEGORIES.map((category) => ({
        value: visitNoteCategoryToken(category),
        label: <LabelOption color={category.color} label={category.display} />,
        keywords: [category.display],
      })),
    ],
    [labels]
  )

  // Drive the checklist from local optimistic state, seeded from the template
  // on open. Each scope-serialized patch sets labels/categories absolutely, so
  // a toggle must build on the prior pending selection — reading server state
  // back would lose any toggle made before its patch round-trips.
  const [selection, setSelection] = useState(() => [
    ...template.labels,
    ...template.categories,
  ])

  const onSelect = useCallback(
    (next: string[], shouldClose?: boolean) => {
      setSelection(next)
      const { labels, categories } = partitionLabelSelection(next)
      patchTemplate({
        instance: template,
        mutateFn: (t) => {
          t.labels = labels
          t.categories = categories
        },
      })
      // cmdk passes `close` true when the row (not the checkbox) is clicked;
      // a row click is a single pick, so dismiss the entire context menu.
      // Radix ContextMenu is uncontrolled (no `open` prop). A synthetic
      // outside pointer-down dismisses the whole menu — both the submenu and
      // the root close, since only the root disables outside pointer events —
      // whereas Escape would only dismiss the topmost (submenu) layer.
      if (shouldClose) {
        document.dispatchEvent(
          new PointerEvent('pointerdown', { bubbles: true })
        )
      }
    },
    [patchTemplate, template]
  )

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className="gap-2">
        <IconTag className="size-4 stroke-muted-foreground" />
        Labels
      </ContextMenuSubTrigger>
      {/* Fixed width matching the note-header label combobox popover (w-72)
       * so a long label can't stretch the menu; long text clamps with an
       * ellipsis inside it. */}
      <ContextMenuSubContent className="w-72 p-0">
        {/* Let the cmdk Command own keyboard navigation/typeahead inside the
         * menu; otherwise Radix's menu typeahead would swallow keystrokes. */}
        <div onKeyDown={(e) => e.stopPropagation()}>
          <ComboboxMultiCommand
            options={options}
            selectedValues={selection}
            onSelect={onSelect}
            searchPlaceholder="Add labels…"
          />
        </div>
      </ContextMenuSubContent>
    </ContextMenuSub>
  )
}
